import React, { useEffect, useState, useRef } from "react";
import { RichText } from "@learningblocks/shared-components/lib/RichText";
import GuidanceIcon from "./img/guidance-icon.svg";
import WarningIcon from "./img/warning-icon.svg";
import ContaminationIcon from "./img/contamination-icon.svg";
import CloseIcon from "./img/close-icon.svg";
import colors from "@learningblocks/protocol-constants/lib/Styles/_exports.module.scss";

import styles from "./Toast.module.scss";

export const ToastType = {
  WARNING: "Warning",
  CONTAMINATION: "Contamination",
  GUIDANCE: "Guidance",
};

const CloseButton = ({ closeToast, position = "right" }) => {
  return (
    <button
      tabIndex={0}
      className={`${styles.toastCloseButton} ${styles[`toastClose${position.charAt(0).toUpperCase() + position.slice(1)}`]}`}
      aria-label="Close alert"
      onKeyDown={closeToast}
      onClick={closeToast}
    >
      <img src={CloseIcon} alt="Close" className={styles.toastCloseIcon} />
    </button>
  );
};

const CustomProgressBar = ({
  animationDuration,
  isPaused,
  closeToast,
  type,
}) => {
  const [progress, setProgress] = useState(100);
  const intervalRef = useRef(null);
  const startTimeRef = useRef(null);
  const pausedTimeRef = useRef(0);
  const totalPausedTimeRef = useRef(0);

  useEffect(() => {
    // Initialize start time on mount
    if (!startTimeRef.current) {
      startTimeRef.current = Date.now();
      totalPausedTimeRef.current = 0;
    }
  }, []);

  useEffect(() => {
    if (!isPaused && progress > 0) {
      // Starting or resuming
      if (pausedTimeRef.current > 0) {
        // Resuming: add paused duration to total paused time
        totalPausedTimeRef.current += Date.now() - pausedTimeRef.current;
        pausedTimeRef.current = 0;
      }

      intervalRef.current = setInterval(() => {
        const elapsed =
          Date.now() - startTimeRef.current - totalPausedTimeRef.current;
        const newProgress = Math.max(
          0,
          100 - (elapsed / animationDuration) * 100
        );

        setProgress(newProgress);

        if (newProgress <= 0) {
          clearInterval(intervalRef.current);
          if (closeToast) closeToast();
        }
      }, 16); // ~60fps
    } else if (isPaused && intervalRef.current) {
      // Pausing
      clearInterval(intervalRef.current);
      pausedTimeRef.current = Date.now();
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPaused, animationDuration, closeToast, progress]);

  const typeClass =
    styles[`customProgressBar${type}`] || styles.customProgressBarGuidance;

  return (
    <div className={`${styles.customProgressBar} ${typeClass}`}>
      <div
        className={styles.customProgressBarFill}
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

const Toast = ({
  type = ToastType.GUIDANCE,
  msg,
  closeToast = () => {},
  showProgress = true,
  animationDuration = 5000,
  isPaused = false,
}) => {
  const toastRef = useRef(null);

  const getIconSrc = () => {
    switch (type) {
      case ToastType.WARNING:
        return WarningIcon;
      case ToastType.CONTAMINATION:
        return ContaminationIcon;
      case ToastType.GUIDANCE:
      default:
        return GuidanceIcon;
    }
  };

  useEffect(() => {
    if (toastRef.current) {
      toastRef.current.focus();
    }
  }, []);

  const typeClass = styles[`toast${type}`] || styles.toastGuidance;
  const containerClasses = `${styles.toastContainer} ${typeClass}`.trim();

  return (
    <div className={containerClasses}>
      <div className={styles.toastIconContainer}>
        <img
          src={getIconSrc()}
          alt="Notification icon"
          className={styles.toastIconImage}
        />
      </div>

      <div
        role="button"
        tabIndex={0}
        aria-label="Close notification"
        className={styles.toastTextContainer}
        data-autofocus
        ref={toastRef}
        onClick={closeToast}
        onKeyDown={closeToast}
      >
        <div className={styles.toastTextMessage}>
          <RichText text={msg} style={{ color: colors.charcoal1 }} />
        </div>
      </div>

      <CloseButton closeToast={closeToast} position="right" />

      {showProgress && (
        <CustomProgressBar
          animationDuration={animationDuration}
          isPaused={isPaused}
          closeToast={closeToast}
          type={type}
        />
      )}
    </div>
  );
};

export default Toast;
